import axios from 'axios';

// Helper function to deduplicate test types
const normalizeTestTypes = (types) => {
  const uniqueTypes = [...new Set(types)];
  return uniqueTypes.map((type, index) => ({
    id: type.toLowerCase().replace(/\s+/g, '-'),
    _id: type.toLowerCase().replace(/\s+/g, '-'),
    name: type,
    title: type,
    color: '#4F46E5', // Consistent color for all test types
    icon: '/test-icon.svg',
  }));
};

// Custom Notebooks API
export const createNotebook = async (name, packId) => {
  try {
    const response = await axios.post(`/api/v2/notebooks/`, { name, packId });
    return response.data;
  } catch (error) {
    // console.error('Error creating notebook:', error);
    throw error;
  }
};

export const getNotebooks = async (packId) => {
  try {
    // console.log(`Fetching notebooks for pack ${packId} from ${API_URL}`);
    const response = await axios.get(`/api/v2/notebooks/${packId}`);
    // console.log('Notebooks response:', response.data);
    return response.data;
  } catch (error) {
    // console.error('Error fetching notebooks:', {
    //   config: error.config,
    //   response: error.response?.data,
    //   message: error.message
    // });
    throw error;
  }
};

export const updateNotebook = async (notebookId, title) => {
  try {
    const response = await axios.patch(`/api/v1/notebooks/${notebookId}`, { title });
    return response.data;
  } catch (error) {
    // console.error('Error updating notebook:', error);
    throw error;
  }
};

export const archiveNotebook = async (notebookId) => {
  try {
    const response = await axios.patch(`/api/v1/notebooks/${notebookId}/archive`);
    return response.data;
  } catch (error) {
    // console.error('Error archiving notebook:', error);
    throw error;
  }
};

// Subjects API
export const getSubjects = async (packId) => {
  try {
    const response = await axios.get(`/api/packs/${packId}/subjects`);
    return response.data.map((subject) => ({
      ...subject,
      color: getSubjectColor(subject.name),
      icon: getSubjectIcon(subject.name),
    }));
  } catch (error) {
    // console.error('Error fetching subjects:', error);
    throw error;
  }
};

// Test Types API
export const getTestTypes = async (packId) => {
  try {
    const response = await axios.get(`/api/packs/${packId}/testTypes`);
    const rawTypes = Array.isArray(response.data.data)
      ? response.data.data
      : Array.isArray(response.data)
        ? response.data
        : [];
    return normalizeTestTypes(rawTypes);
  } catch (error) {
    // console.error('Error fetching test types:', error);
    throw error;
  }
};

// Chapter-wise Notebooks API
export const getChapterWiseNotebooks = async (packId, subjectId) => {
  try {
    const response = await axios.get(`/api/v2/notebooks/${packId}/${subjectId}/chapter-wise`);
    // console.log('Chapter-wise notebooks response:', response.data);
    return response.data;
  } catch (error) {
    // console.error('Error fetching chapter-wise notebooks:', error);
    throw error;
  }
};

export const getChapterWiseNotebookDetails = async (chapterId, questionIds, params) => {
  try {
    const response = await axios.post(
      `/api/v2/notebooks/${chapterId}/chapter-wise-detailed`,
      { questionIds: questionIds },
      { params },
    );
    return response.data;
  } catch (error) {
    // console.error('Error fetching chapter-wise notebook details:', error);
    throw error;
  }
};

// Test-wise Notebooks API
export const getTestWiseNotebooks = async (packId, testType) => {
  try {
    const response = await axios.get(`/api/v2/notebooks/${packId}/test-wise`, {
      params: { testType },
    });

    // Handle response structure
    const responseData = response.data.data || response.data;
    if (!Array.isArray(responseData)) {
      // console.error('Invalid test-wise notebooks response structure:', responseData);
      return [];
    }

    return responseData.map((notebook, index) => ({
      id: notebook.testId || notebook._id || `test-${index}`,
      _id: notebook.testId || notebook._id,
      testId: notebook.testId || notebook._id,
      title: notebook.title || `Test ${index + 1}`,
      name: notebook.title || `Test ${index + 1}`,
      questionCount: notebook.questions ? notebook.questions.length : 0,
      questions: notebook.questions || [],
      testType: testType,
      lastUsed: notebook.lastUsed || new Date().toISOString(),
      createdAt: notebook.createdAt || new Date().toISOString(),
      updatedAt: notebook.updatedAt || new Date().toISOString(),
      type: 'test',
      packId: packId,
      isArchived: false,
      isPinned: false,
    }));
  } catch (error) {
    // console.error('Error fetching test-wise notebooks:', {
    //   config: error.config,
    //   response: error.response?.data,
    //   message: error.message
    // });

    if (error.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

const formatAndUppercase = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text.replace(/([a-z])([A-Z])/g, '$1 $2').toUpperCase();
};

export const getTestWiseNotebookDetails = async (testId, questionIds = [], params = {}) => {
  try {
    if (!testId) {
      throw new Error('Test ID is required');
    }

    const queryParams = {
      ...(params.subject && { subject: params.subject }),
      ...(params.correct !== undefined && { correct: params.correct }),
      ...(params.incorrect !== undefined && { incorrect: params.incorrect }),
      ...(params.unattempted !== undefined && { unattempted: params.unattempted }),
      ...(params.communitySolution !== undefined && { communitySolution: params.communitySolution }),
      offset: params.offset || 0,
      limit: params.limit || 10,
    };

    const response = await axios.post(
      `/api/v2/notebooks/${testId}/test-wise-detailed`,
      { questionIds },
      { params: queryParams },
    );

    const responseData = response.data.data || response.data;

    return {
      questions:
        responseData.answers?.map((answer, index) => ({
          id: `Q${index + 1}`,
          testId: answer.test || null,
          questionId: answer.question?._id,
          subject: answer.subject,
          chapter: answer.chapter,
          question: answer.question.question?.text,
          level: answer.question?.level,
          options: answer.question?.options || [],
          correctValue: answer.question?.correctValue,
          solution: answer.question?.solution,
          videoSolution: answer.question?.videoSolution,
          status: formatAndUppercase(answer.status),
          attemptOverview: answer.attemptOverview,
          isCorrect: answer.isCorrect,
          timeTaken: answer.timeTaken,
          type: answer.type,
          optionOrSoln: answer.optionOrSoln,
          notes: answer.notes,
          user_question_tags: answer.user_question_tags,
        })) || [],
      subjects: responseData.subjects || [],
      stats: {
        correct: responseData.totalCorrect || 0,
        wrong: responseData.totalIncorrect || 0,
        unattempted: responseData.totalUnattempted || 0,
        totalCorrect: responseData.totalCorrect || 0,
        totalIncorrect: responseData.totalIncorrect || 0,
        totalUnattempted: responseData.totalUnattempted || 0,
      },
      totalPages: response.data.totalPages || Math.ceil((responseData.totalQuestions || 0) / (queryParams.limit || 10)),
      currentPage: Math.floor((queryParams.offset || 0) / (queryParams.limit || 10)) + 1,
    };
  } catch (error) {
    throw error;
  }
};

// Helper functions
const getSubjectColor = (subjectName) => {
  const colors = {
    Physics: '#4F46E5',
    Chemistry: '#10B981',
    Mathematics: '#F59E0B',
    Biology: '#EC4899',
  };
  return colors[subjectName] || '#4F46E5';
};

const getSubjectIcon = (subjectName) => {
  const icons = {
    Physics: '/physics-icon.svg',
    Chemistry: '/chemistry-icon.svg',
    Mathematics: '/math-icon.svg',
    Biology: '/biology-icon.svg',
  };
  return icons[subjectName] || '/subject-icon.svg';
};

// Custom Notebook Details API

export const getCustomNotebookDetails = async (notebookId, params) => {
  try {
    const response = await axios.get(`/api/v2/notebooks/${notebookId}/detailed`, { params });
    // console.log('Custom notebook details response:', response.data);
    return response.data;
  } catch (error) {
    // console.error('Error fetching custom notebook details:', error);
    throw error;
  }
};
